/*
 * This file is part of glowingblue/orell-rezensionen.
 *
 * Copyright (c) 2022 Glowing Blue AG.
 * Authors: Rafael Horvat.
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import app from 'flarum/forum/app';
import { extend } from 'flarum/common/extend';
import ArticleHero from 'flarum/orell-articles/components/ArticleHero';
import LinkButton from 'flarum/common/components/LinkButton';
import icon from 'flarum/common/helpers/icon';
import ArticleRating from '../components/ArticleRating';
import CreateReviewPrimaryButton from '../components/CreateReviewPrimaryButton';
import Link from 'flarum/common/components/Link';
import { utils } from '@glowingblue-orell-thalia-common';
import type { VdomParent } from '@glowingblue-orell-thalia-common/utils/vdom';
import type Mithril from 'mithril';

const { findChild } = utils.vdom;

/**
 * Wraps the first child of `.${className}` in a link to the article page.
 *
 * `ArticleHero` has no seam for this — both targets (the `<ul>` from `items()` and the cover
 * `<img>`) are rendered in `view()`, and its ItemLists only address entries *within* them. The
 * wrap is positional, so a miss gives up quietly rather than throwing mid-render.
 */
function wrapFirstChildInLink(tree: Mithril.Children, className: string, href: string, linkClassName?: string): void {
	const target = findChild(tree as VdomParent, className, true);

	if (!target || !Array.isArray(target.children) || !target.children.length) {
		return;
	}

	// The live child array of a rendered vnode, mutated in place so Mithril picks the wrap up.
	const children = target.children as Mithril.ChildArray;

	children[0] = (
		<Link href={href} className={linkClassName}>
			{children[0]}
		</Link>
	);
}

export default function () {
	extend(ArticleHero.prototype, 'view', function (vnode) {
		const { isReview, article } = this.attrs;
		if (!isReview) {
			return;
		}

		const href = app.route.of_article(article);

		wrapFirstChildInLink(vnode, 'ArticleHero-items--wrapper', href, 'ArticleHero-items');
		wrapFirstChildInLink(vnode, 'ArticleHero-cover', href);
	});

	extend(ArticleHero.prototype, 'items', function (items) {
		const { isReview, article } = this.attrs;
		if (isReview) {
			items.add(
				'article-link',
				<LinkButton icon="far fa-chevron-left" href={app.route.of_article(article)}>
					{app.translator.trans('glowingblue-orell-rezensionen.forum.review_page.article_link_label')}
				</LinkButton>,
				12
			);
			items.setContent('author', <div>{article.author()}</div>);
		} else {
			const allReviewsCount = article.allReviewsCount();
			if (allReviewsCount > 0) {
				items.add(
					'reviews-avg',
					<div className="ArticlePage-reviews-avg">
						<ArticleRating value={article.rating()} />
						{app.translator.trans('glowingblue-orell-rezensionen.lib.reviews', { reviews: allReviewsCount })}
					</div>,
					4
				);
			} else {
				items.add(
					'no-reviews',
					<div className="ArticlePage-no_reviews">
						<CreateReviewPrimaryButton
							article={article}
							label={
								<span>
									{app.translator.trans('glowingblue-orell-rezensionen.forum.article_page.be_first')}
									{icon('far fa-pencil')}
								</span>
							}
						/>
					</div>,
					4
				);
			}
		}
	});
}
